import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputBase,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AppTablePagination from "../../component/common/appTablePagination";
import SearchIcon from "@mui/icons-material/Search";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";
import returnRequestService from "../../../services/ReturnRequestService";
import saleOrderServices, {
  parseCatalogProducts,
} from "../../../services/SalesOrderService";
import customerServices from "../../../services/CustomerService";
import { ReturnType } from "../../../types/returnManagement";
import { CURRENCY_SYMBOL } from "../../../constants/currency";
import { showToast } from "../../component/common/toastNotification";
import { localizeReturnReason } from "../../../helper/localizeReturnReason";
import {
  NJIM,
  glassCardSx,
  pageShellSx,
  primaryBtnSx,
  softFieldSx,
  CONTROL_HEIGHT,
  searchFieldSx,
} from "../../../theme/njimTheme";
import {
  formatSubmissionDate,
  getSubmissionDateMs,
  matchesSubmissionDateRange,
} from "../../../utils/submissionDateFilter";

interface WorkflowReturn extends ReturnType {
  customerId?: string;
  customerNo?: string;
  customerCode?: string;
  customerName?: string;
  company?: string;
  supervisor?: string;
}

type WorkflowStage = "pending" | "approved" | "rejected" | "all";

const WORKFLOW_FILTER_GAP = "20px";

const workflowFilterLabelSx = {
  fontSize: 13,
  color: NJIM.textPrimary,
  fontWeight: 600,
  mb: 1,
} as const;

const workflowFilterGridSx = {
  display: "grid",
  width: "100%",
  boxSizing: "border-box",
  gap: WORKFLOW_FILTER_GAP,
  "& > *": {
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
} as const;

const workflowOutlinedFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    height: CONTROL_HEIGHT,
    background: "rgba(255,255,255,0.55)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 10px 26px rgba(2,6,23,0.06)",
    fontWeight: 700,
    color: NJIM.textPrimary,
    "& fieldset": { borderColor: "rgba(148,163,184,0.35)" },
    "&:hover fieldset": { borderColor: "rgba(22,45,58,0.25)" },
    "&.Mui-focused fieldset": { borderColor: NJIM.ink },
  },
  "& .MuiSelect-select": {
    fontWeight: 800,
    color: NJIM.textPrimary,
    display: "flex",
    alignItems: "center",
    height: CONTROL_HEIGHT,
    boxSizing: "border-box",
  },
} as const;

const WorkflowPortal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateSort, setDateSort] = useState<"oldest" | "newest">("oldest");
  const [activeStage, setActiveStage] = useState<WorkflowStage>("pending");
  const [returnsList, setReturnsList] = useState<WorkflowReturn[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedDetails, setSelectedDetails] = useState<WorkflowReturn | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [predefinedReason, setPredefinedReason] = useState("");
  const [listPage, setListPage] = useState(0);

  const PREDEFINED_REASONS_KEYS = [
    "damagedInTransit",
    "expiredProduct",
    "wrongItemShipped",
    "qualityIssue",
    "duplicateOrder",
    "customerRequestCancel",
  ] as const;
  const [priceByItemId, setPriceByItemId] = useState<Record<string, number>>({});
  // Maps customer MongoDB id → { name, code } for consistent display
  const [customerInfoMap, setCustomerInfoMap] = useState<Record<string, { name: string; code: string }>>({});
  const [productNameMap, setProductNameMap] = useState<Record<string, string>>({});
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch customer info by direct ID lookup — batched to avoid ERR_INSUFFICIENT_RESOURCES
  const fetchCustomerInfoForIds = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    const BATCH_SIZE = 5;
    const patch: Record<string, { name: string; code: string }> = {};
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map((id) => customerServices.getCustomerById(id))
      );
      results.forEach((result) => {
        if (result.status === "fulfilled") {
          const c = result.value?.data as Record<string, unknown> | undefined;
          if (c) {
            const id   = String(c.id   ?? c.Id   ?? "");
            const code = String(c.customerCode ?? c.CustomerCode ?? c.title ?? c.Title ?? "");
            const name = String(c.customerName ?? c.CustomerName ?? "");
            if (id) patch[id] = { name, code };
            if (code) patch[code] = { name, code };
          }
        }
      });
    }
    if (Object.keys(patch).length > 0) {
      setCustomerInfoMap((prev) => ({ ...prev, ...patch }));
    }
  }, []);

  useEffect(() => {
    const loadProductData = async () => {
      try {
        const response = await saleOrderServices.getProductCatalog();
        const products = parseCatalogProducts(response?.data);

        const nameMap: Record<string, string> = {};
        const priceMap: Record<string, number> = {};

        products.forEach((p) => {
          const id   = String(p.id   ?? p.Id   ?? "").trim();
          const code = String(p.code ?? p.Code ?? "").trim();
          const name = String(p.name ?? p.Name ?? p.title ?? p.Title ?? "").trim();
          const price = Number(p.price ?? p.Price ?? 0);

          if (id) {
            if (name) nameMap[id] = name;
            if (price) priceMap[id] = price;
          }
          if (code) {
            if (name) nameMap[code] = name;
            if (price) priceMap[code] = price;
          }
        });

        setProductNameMap(nameMap);
        setPriceByItemId(priceMap);
      } catch {
        setPriceByItemId({});
      }
    };
    loadProductData();
  }, []);

  const fetchReturns = useCallback(async (keyword = "") => {
    try {
      setLoading(true);
      const response = await returnRequestService.getReturnList({
        keyword,
        pageNumber: 1,
        pageSize: 5000,
        sortBy: "RequestDate",
        sortDescending: false,
      });
      const items: WorkflowReturn[] = response?.data?.items ?? [];
      setReturnsList(items);

      // Collect unique customer IDs from this batch and fetch their info
      const uniqueIds: string[] = [];
      const seen: Record<string, boolean> = {};
      items.forEach((r) => {
        const id = r.customerId ?? "";
        if (id && !seen[id]) {
          seen[id] = true;
          uniqueIds.push(id);
        }
      });
      fetchCustomerInfoForIds(uniqueIds);

      if (!selectedId && items.length > 0) {
        setSelectedId(String(items[0].id ?? ""));
      } else if (selectedId && !items.some((item) => String(item.id ?? "") === selectedId)) {
        setSelectedId(items.length > 0 ? String(items[0].id ?? "") : "");
      }
    } catch (error) {
      console.error("Error fetching workflow returns:", error);
      setReturnsList([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, fetchCustomerInfoForIds]);

  useEffect(() => {
    fetchReturns(debouncedSearchTerm);
  }, [debouncedSearchTerm, fetchReturns]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!selectedId) {
        setSelectedDetails(null);
        return;
      }
      try {
        const response = await returnRequestService.getWorkflowById(selectedId);
        setSelectedDetails((response?.data ?? null) as WorkflowReturn | null);
      } catch (error) {
        console.error("Error fetching workflow details:", error);
        setSelectedDetails(null);
      }
    };

    fetchDetails();
  }, [selectedId]);

  const normalizeStatus = useCallback((status: string | number | undefined) => {
    const enumMap: Record<string, string> = {
      "0": "initiated", "1": "pending", "2": "approved",
      "3": "rejected", "4": "cancelled",
    };
    const raw = String(status ?? "").trim().toLowerCase();
    return enumMap[raw] ?? raw;
  }, []);

  const pendingStatuses = useMemo(() => new Set(["pending", "initiated"]), []);
  const approvedStatuses = useMemo(() => new Set(["approved", "completed"]), []);
  const rejectedStatuses = useMemo(() => new Set(["rejected", "cancelled"]), []);

  const dateFilteredReturns = useMemo(() => {
    return returnsList.filter((item) => {
      if (companyFilter !== "all" && String(item.company ?? "").toLowerCase() !== companyFilter.toLowerCase()) {
        return false;
      }
      return matchesSubmissionDateRange(item, dateFrom, dateTo);
    });
  }, [returnsList, companyFilter, dateFrom, dateTo]);

  const stageFilteredReturns = useMemo(() => {
    if (activeStage === "all") return dateFilteredReturns;
    return dateFilteredReturns.filter((item) => {
      const status = normalizeStatus(item.status);
      if (activeStage === "pending") return pendingStatuses.has(status);
      if (activeStage === "approved") return approvedStatuses.has(status);
      if (activeStage === "rejected") return rejectedStatuses.has(status);
      return true;
    });
  }, [dateFilteredReturns, activeStage, pendingStatuses, approvedStatuses, rejectedStatuses, normalizeStatus]);

  const companyOptions = useMemo(() => {
    const options = new Set<string>();
    dateFilteredReturns.forEach((item) => {
      const company = String(item.company ?? "").trim();
      if (company) options.add(company);
    });
    return Array.from(options);
  }, [dateFilteredReturns]);

  const filteredReturns = useMemo(() => {
    const sorted = [...stageFilteredReturns];
    sorted.sort((a, b) => {
      const dateA = getSubmissionDateMs(a) ?? 0;
      const dateB = getSubmissionDateMs(b) ?? 0;
      return dateSort === "oldest" ? dateA - dateB : dateB - dateA;
    });
    return sorted;
  }, [stageFilteredReturns, dateSort]);

  useEffect(() => {
    setListPage(0);
  }, [dateFrom, dateTo, dateSort, companyFilter, activeStage]);

  useEffect(() => {
    if (filteredReturns.length === 0) {
      if (selectedId) setSelectedId("");
      return;
    }
    if (!filteredReturns.some((item) => String(item.id ?? "") === selectedId)) {
      setSelectedId(String(filteredReturns[0].id ?? ""));
    }
  }, [filteredReturns, selectedId]);

  const selectedItem = useMemo(() => {
    const fromList =
      filteredReturns.find((item) => String(item.id ?? "") === selectedId) ??
      returnsList.find((item) => String(item.id ?? "") === selectedId) ??
      null;

    if (!selectedDetails || String(selectedDetails.id ?? "") !== selectedId) {
      return fromList;
    }

    // Prefer list data when it has a newer status (e.g. right after approve/reject).
    const listStatus = normalizeStatus(fromList?.status);
    const detailStatus = normalizeStatus(selectedDetails.status);
    if (fromList && listStatus !== detailStatus) {
      return fromList;
    }

    return selectedDetails;
  }, [selectedDetails, selectedId, filteredReturns, returnsList, normalizeStatus]);

  const handleCompanyChange = (event: SelectChangeEvent) => {
    setCompanyFilter(event.target.value);
    setListPage(0);
  };

  const getStatusLabel = (status: string | number | undefined) => {
    let normalizedStatus = normalizeStatus(status).replace(/\s+/g, "");
    // Treat "initiated" the same as "pending" for display purposes
    if (normalizedStatus === "initiated") normalizedStatus = "pending";
    const fallback = String(status ?? "").replace(/([a-z])([A-Z])/g, "$1 $2");
    return t(`status.${normalizedStatus}`, { defaultValue: fallback || "-" });
  };

  const getDisplayAmount = useCallback((item: WorkflowReturn) => {
    const apiAmount = Number(item.amount ?? 0);
    if (apiAmount > 0) return apiAmount;
    return (item.items ?? []).reduce((sum, lineItem) => {
      const price = priceByItemId[String(lineItem.itemId ?? "").trim()] ?? 0;
      return sum + price * Number(lineItem.quantity ?? 0);
    }, 0);
  }, [priceByItemId]);

  const formatDisplayAmount = useCallback((item: WorkflowReturn) =>
    `${CURRENCY_SYMBOL} ${getDisplayAmount(item).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`, [getDisplayAmount]);

  const getStatusChipStyle = useCallback((status: string | number | undefined) => {
    const s = normalizeStatus(status);
    if (approvedStatuses.has(s)) {
      return { bg: "rgba(5,150,105,0.14)", color: NJIM.success, border: "rgba(5,150,105,0.18)" };
    }
    if (rejectedStatuses.has(s)) {
      return { bg: "rgba(220,38,38,0.14)", color: NJIM.danger, border: "rgba(220,38,38,0.18)" };
    }
    return { bg: "rgba(245,158,11,0.14)", color: NJIM.warning, border: "rgba(245,158,11,0.18)" };
  }, [normalizeStatus, approvedStatuses, rejectedStatuses]);

  const resolveCustomerInfo = useCallback((item: WorkflowReturn) => {
    const keys = [item.customerId, item.customerCode, item.customerNo].filter(
      (k): k is string => Boolean(k)
    );
    for (const key of keys) {
      const entry = customerInfoMap[key];
      if (entry) return entry;
    }
    return null;
  }, [customerInfoMap]);

  const resolveCustomerName = useCallback((item: WorkflowReturn): string => {
    const info = resolveCustomerInfo(item);
    if (info?.name) return info.name;
    return item.customerName && item.customerName !== "-" ? item.customerName : "-";
  }, [resolveCustomerInfo]);

  const resolveCustomerCode = useCallback((item: WorkflowReturn): string => {
    const info = resolveCustomerInfo(item);
    if (info?.code) return info.code;
    // Fall back to whichever code field the return record has
    return item.customerNo ?? item.customerCode ?? item.customerId ?? "-";
  }, [resolveCustomerInfo]);

  const pendingCount = dateFilteredReturns.filter((item) =>
    pendingStatuses.has(normalizeStatus(item.status))
  ).length;
  const approvedCount = dateFilteredReturns.filter((item) =>
    approvedStatuses.has(normalizeStatus(item.status))
  ).length;
  const rejectedCount = dateFilteredReturns.filter((item) =>
    rejectedStatuses.has(normalizeStatus(item.status))
  ).length;

  const paginatedReturns = useMemo(() => {
    const start = listPage * ITEMS_PER_PAGE;
    return filteredReturns.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredReturns, listPage]);

  const selectedItems = selectedItem?.items ?? [];

  const applyLocalStatusUpdate = useCallback(
    (actedOnId: string, newStatus: "Approved" | "Rejected") => {
      setReturnsList((prev) => {
        const updated = prev.map((item) =>
          String(item.id ?? "") === actedOnId ? { ...item, status: newStatus } : item
        );

        const pending = updated.filter((item) =>
          pendingStatuses.has(normalizeStatus(item.status))
        );
        const next =
          pending.find((item) => String(item.id ?? "") !== actedOnId) ?? pending[0] ?? null;

        queueMicrotask(() => {
          setSelectedId(next ? String(next.id ?? "") : "");
          setSelectedDetails(null);
          setListPage(0);
        });

        return updated;
      });
    },
    [pendingStatuses, normalizeStatus]
  );

  const handleApprove = useCallback(async () => {
    if (!selectedItem || !selectedItem.id) return;
    const actedOnId = String(selectedItem.id);
    setActionLoading(true);
    try {
      await returnRequestService.approveReturn(actedOnId);
      showToast(t("workflowPortal.approvedSuccess"), "success");
      applyLocalStatusUpdate(actedOnId, "Approved");
      await fetchReturns(debouncedSearchTerm);
    } catch {
      showToast(t("workflowPortal.approvedError"), "error");
    } finally {
      setActionLoading(false);
    }
  }, [selectedItem, applyLocalStatusUpdate, fetchReturns, debouncedSearchTerm, t]);

  const handleReject = useCallback(async () => {
    if (!selectedItem || !selectedItem.id) return;
    const actedOnId = String(selectedItem.id);
    setActionLoading(true);
    try {
      await returnRequestService.rejectReturn(
        actedOnId,
        predefinedReason,
        rejectReason.trim() || undefined
      );
      showToast(t("workflowPortal.rejectedSuccess"), "success");
      setRejectDialogOpen(false);
      setRejectReason("");
      setPredefinedReason("");
      applyLocalStatusUpdate(actedOnId, "Rejected");
      await fetchReturns(debouncedSearchTerm);
    } catch {
      showToast(t("workflowPortal.rejectedError"), "error");
    } finally {
      setActionLoading(false);
    }
  }, [
    selectedItem,
    predefinedReason,
    rejectReason,
    applyLocalStatusUpdate,
    fetchReturns,
    debouncedSearchTerm,
    t,
  ]);

  return (
    <Box sx={{ direction: isRTL ? "rtl" : "ltr", ...pageShellSx }}>
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Typography
          sx={{
            fontSize: { xs: 28, md: 36 },
            fontWeight: 900,
            color: NJIM.textPrimary,
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
          }}
        >
          {t("workflowPortal.title")}
        </Typography>
        <Typography sx={{ fontSize: 15, color: NJIM.textSecondary, mt: 0.75, mb: 2.5, fontWeight: 600 }}>
          {t("workflowPortal.subtitle")}
        </Typography>

      <Card sx={{ ...glassCardSx, p: { xs: 2, md: 2.25 }, mb: 2, overflow: "visible" }}>
        <Box
          sx={{
            ...workflowFilterGridSx,
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 2fr) minmax(0, 1fr)",
            },
            mb: 2.5,
          }}
        >
          <Box>
            <Typography sx={workflowFilterLabelSx}>{t("common.search")}</Typography>
            <Box
              component="form"
              onSubmit={(e) => e.preventDefault()}
              sx={{
                ...searchFieldSx,
                ...softFieldSx,
                px: 1.2,
                height: CONTROL_HEIGHT,
                boxSizing: "border-box",
              }}
            >
              <SearchIcon sx={{ color: NJIM.textMuted, fontSize: 18, flexShrink: 0 }} />
              <InputBase
                placeholder={t("workflowPortal.searchPlaceholder")}
                sx={{
                  width: "100%",
                  fontSize: 14,
                  fontWeight: 700,
                  color: NJIM.textPrimary,
                  "& input::placeholder": { color: NJIM.textMuted, fontWeight: 700, opacity: 1 },
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>
          </Box>
          <Box>
            <Typography sx={workflowFilterLabelSx}>{t("workflowPortal.filterByCompany")}</Typography>
            <Select
              value={companyFilter}
              onChange={handleCompanyChange}
              fullWidth
              size="small"
              sx={workflowOutlinedFieldSx}
            >
              <MenuItem value="all">{t("workflowPortal.allCompanies")}</MenuItem>
              {companyOptions.map((company) => (
                <MenuItem key={company} value={company}>
                  {company}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 13, color: NJIM.textPrimary, fontWeight: 700 }}>
            {t("workflowPortal.filterBySubmissionDate")}
          </Typography>
          {(dateFrom || dateTo) && (
            <Button
              size="small"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setListPage(0);
              }}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
                color: NJIM.textSecondary,
                borderRadius: "10px",
                border: "1px solid rgba(148,163,184,0.35)",
                px: 2,
                py: 0.8,
                "&:hover": {
                  background: "rgba(239,68,68,0.05)",
                  color: NJIM.danger,
                  borderColor: "rgba(239,68,68,0.3)",
                },
              }}
            >
              {t("workflowPortal.clearDates", { defaultValue: "Clear Dates" })}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            ...workflowFilterGridSx,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            alignItems: "end",
          }}
        >
          <Box>
            <Typography sx={workflowFilterLabelSx}>{t("workflowPortal.submissionDateFrom")}</Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setListPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={workflowOutlinedFieldSx}
            />
          </Box>
          <Box>
            <Typography sx={workflowFilterLabelSx}>{t("workflowPortal.submissionDateTo")}</Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setListPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateFrom || undefined }}
              sx={workflowOutlinedFieldSx}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1", md: "auto" } }}>
            <Typography sx={workflowFilterLabelSx}>{t("workflowPortal.sortBySubmissionDate")}</Typography>
            <Select
              fullWidth
              size="small"
              value={dateSort}
              onChange={(e: SelectChangeEvent) => {
                setDateSort(e.target.value as "oldest" | "newest");
                setListPage(0);
              }}
              sx={workflowOutlinedFieldSx}
            >
              <MenuItem value="oldest">{t("workflowPortal.sortOldestFirst")}</MenuItem>
              <MenuItem value="newest">{t("workflowPortal.sortNewestFirst")}</MenuItem>
            </Select>
          </Box>
        </Box>
      </Card>

      <Card
        sx={{
          ...glassCardSx,
          p: 0.75,
          mb: 2,
          borderRadius: 999,
          background: "rgba(255,255,255,0.55)",
          boxShadow: "0 16px 45px rgba(2,6,23,0.08)",
        }}
      >
        <Grid container>
          {([
            { key: "pending" as const, label: t("workflowPortal.pendingTab"), count: pendingCount, icon: <ShieldOutlinedIcon sx={{ fontSize: 16 }} /> },
            { key: "approved" as const, label: t("workflowPortal.approvedTab"), count: approvedCount, icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> },
            { key: "rejected" as const, label: t("workflowPortal.rejectedTab"), count: rejectedCount, icon: <HighlightOffIcon sx={{ fontSize: 16 }} /> },
            { key: "all" as const, label: t("workflowPortal.allTab"), count: dateFilteredReturns.length, icon: <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} /> },
          ]).map((tab) => (
            <Grid item xs={3} key={tab.key}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                justifyContent="center"
                onClick={() => {
                  setActiveStage(tab.key);
                  setListPage(0);
                }}
                sx={{
                  cursor: "pointer",
                  py: 1.05,
                  borderRadius: 999,
                  transition: "all 0.2s ease",
                  background:
                    activeStage === tab.key
                      ? NJIM.walletHero
                      : NJIM.muted,
                  boxShadow:
                    activeStage === tab.key ? "0 4px 14px rgba(22,51,63,0.25)" : "none",
                  "& .MuiSvgIcon-root": {
                    color: activeStage === tab.key ? NJIM.white : NJIM.textSecondary,
                  },
                }}
              >
                {tab.icon}
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: 11, md: 13 },
                    color: activeStage === tab.key ? NJIM.white : NJIM.textPrimary,
                  }}
                >
                  {tab.label}
                </Typography>
                <Box
                  sx={{
                    minWidth: 22,
                    textAlign: "center",
                    borderRadius: "6px",
                    px: 0.6,
                    py: 0.15,
                    background:
                      activeStage === tab.key
                        ? "rgba(255,255,255,0.2)"
                        : NJIM.mintWash,
                    color: activeStage === tab.key ? NJIM.white : NJIM.ink,
                    fontWeight: 900,
                    fontSize: 11,
                  }}
                >
                  {tab.count}
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ ...glassCardSx, p: 0 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid rgba(148,163,184,0.22)" }}>
              <Typography sx={{ fontSize: 22, fontWeight: 900, color: NJIM.textPrimary }}>
                {{
                  pending: t("workflowPortal.pendingReturns"),
                  approved: `${t("workflowPortal.approvedTab")} ${t("workflowPortal.returns", { defaultValue: "Returns" })}`,
                  rejected: `${t("workflowPortal.rejectedTab")} ${t("workflowPortal.returns", { defaultValue: "Returns" })}`,
                  all: `${t("workflowPortal.allTab")} ${t("workflowPortal.returns", { defaultValue: "Returns" })}`,
                }[activeStage]} ({filteredReturns.length})
              </Typography>
              <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mt: 0.5, fontWeight: 600 }}>
                {dateSort === "oldest"
                  ? t("workflowPortal.pendingOldestFirst")
                  : t("workflowPortal.sortedNewestFirst")}
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress size={36} sx={{ color: NJIM.ink }} />
              </Box>
            ) : filteredReturns.length === 0 ? (
              <Box sx={{ textAlign: "center", color: NJIM.textMuted, py: 5 }}>
                {t("common.noData")}
              </Box>
            ) : (
              <>
              {paginatedReturns.map((item) => (
              <Box
                key={String(item.id)}
                onClick={() => setSelectedId(String(item.id ?? ""))}
                sx={{
                  p: 2,
                  borderInlineStart:
                    selectedId === String(item.id ?? "")
                      ? `4px solid ${NJIM.ink}`
                      : "4px solid transparent",
                  borderBottom: "1px solid rgba(148,163,184,0.18)",
                  cursor: "pointer",
                  background:
                    selectedId === String(item.id ?? "")
                      ? NJIM.mintWash
                      : "rgba(255,255,255,0.0)",
                  transition: "background 0.18s ease",
                  "&:hover": {
                    background:
                      selectedId === String(item.id ?? "")
                        ? NJIM.mintWash
                        : "rgba(15,23,42,0.02)",
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.8 }}
                >
                  <Typography sx={{ fontWeight: 900, color: NJIM.textPrimary }}>
                    {item.returnNumber ?? item.title ?? item.id}
                  </Typography>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.2,
                      borderRadius: 999,
                      background: getStatusChipStyle(item.status).bg,
                      color: getStatusChipStyle(item.status).color,
                      fontSize: 12,
                      fontWeight: 900,
                      border: `1px solid ${getStatusChipStyle(item.status).border}`,
                    }}
                  >
                    {getStatusLabel(item.status)}
                  </Box>
                </Stack>

                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                  {t("workflowPortal.customerCode")}:{" "}
                  <Box component="span" sx={{ color: NJIM.textPrimary, fontWeight: 600 }}>
                    {resolveCustomerCode(item)}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                  {t("workflowPortal.customerName")}:{" "}
                  <Box component="span" sx={{ color: NJIM.textPrimary, fontWeight: 600 }}>
                    {resolveCustomerName(item)}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                  {t("workflowPortal.submissionDate")}:{" "}
                  <Box component="span" sx={{ color: NJIM.textPrimary, fontWeight: 600 }}>
                    {formatSubmissionDate(item.requestDate)}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                  {t("workflowPortal.value")}:{" "}
                  <Box component="span" sx={{ color: NJIM.textPrimary, fontWeight: 700 }}>
                    {formatDisplayAmount(item)}
                  </Box>
                </Typography>
              </Box>
            ))}
              {filteredReturns.length > ITEMS_PER_PAGE && (
                <AppTablePagination
                  component="div"
                  count={filteredReturns.length}
                  page={listPage}
                  onPageChange={(_, newPage) => setListPage(newPage)}
                  rowsPerPage={ITEMS_PER_PAGE}
                  rowsPerPageOptions={[]}
                  sx={{
                    borderTop: "1px solid rgba(148,163,184,0.18)",
                    "& .MuiTablePagination-toolbar": { minHeight: 42 },
                    "& .MuiTablePagination-displayedRows": { fontSize: 12, fontWeight: 700, color: NJIM.textSecondary },
                  }}
                />
              )}
              </>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ ...glassCardSx, p: { xs: 2, md: 2.25 } }}>
            {!selectedItem ? (
              <Box sx={{ textAlign: "center", color: NJIM.textMuted, py: 8 }}>
                {t("common.noData")}
              </Box>
            ) : (
              <>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: NJIM.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
                  {t("workflowPortal.returnReference", { defaultValue: "Return Reference" })}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                  <Typography sx={{ fontSize: { xs: 28, md: 38 }, fontWeight: 950, color: NJIM.textPrimary }}>
                    {selectedItem.returnNumber ?? selectedItem.title ?? selectedItem.id}
                  </Typography>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.2,
                      borderRadius: 999,
                      background: getStatusChipStyle(selectedItem.status).bg,
                      color: getStatusChipStyle(selectedItem.status).color,
                      fontSize: 12,
                      fontWeight: 900,
                      border: `1px solid ${getStatusChipStyle(selectedItem.status).border}`,
                    }}
                  >
                    {getStatusLabel(selectedItem.status)}
                  </Box>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.2,
                      borderRadius: 999,
                      background: "rgba(167,139,250,0.14)",
                      color: NJIM.info,
                      fontSize: 12,
                      fontWeight: 900,
                      border: "1px solid rgba(167,139,250,0.18)",
                    }}
                  >
                    {selectedItem.company ?? "-"}
                  </Box>
                </Stack>
                <Typography sx={{ color: NJIM.textSecondary, fontWeight: 600 }}>
                  {t("workflowPortal.order")}: {selectedItem.orderNumber ?? "-"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: isRTL ? "left" : "right" }}>
                <Typography sx={{ fontSize: { xs: 34, md: 46 }, fontWeight: 950, color: NJIM.ink, lineHeight: 1 }}>
                  {formatDisplayAmount(selectedItem)}
                </Typography>
                <Typography sx={{ color: NJIM.textSecondary, fontWeight: 600 }}>
                  {t("workflowPortal.totalValue")}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{t("workflowPortal.customerCode")}</Typography>
                <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>{resolveCustomerCode(selectedItem)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{t("workflowPortal.customerName")}</Typography>
                <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>{resolveCustomerName(selectedItem)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{t("workflowPortal.submissionDate")}</Typography>
                <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>{formatSubmissionDate(selectedItem.requestDate)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>{t("workflowPortal.supervisor")}</Typography>
                <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>{selectedItem.supervisor ?? "-"}</Typography>
              </Grid>
            </Grid>

            {selectedItem.additionalNotes && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: "12px",
                  background: "rgba(59,130,246,0.06)",
                  border: "1px solid rgba(59,130,246,0.18)",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                }}
              >
                <Box sx={{ fontSize: 18, color: NJIM.info, flexShrink: 0, mt: "2px" }}>💬</Box>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.info, mb: 0.3 }}>
                    {t("workflowPortal.submissionNotes")}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: NJIM.textPrimary }}>
                    {selectedItem.additionalNotes}
                  </Typography>
                </Box>
              </Box>
            )}

            {(selectedItem.rejectionReason || selectedItem.rejectionNote) && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: "12px",
                  background: "rgba(220,38,38,0.06)",
                  border: "1px solid rgba(220,38,38,0.18)",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                }}
              >
                <Box sx={{ fontSize: 18, color: NJIM.danger, flexShrink: 0, mt: "2px" }}>✕</Box>
                <Box sx={{ width: "100%" }}>
                  {selectedItem.rejectionReason && (
                    <>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.danger, mb: 0.3 }}>
                        {t("workflowPortal.rejectionReason")}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: NJIM.danger }}>
                        {selectedItem.rejectionReason}
                      </Typography>
                    </>
                  )}
                  {selectedItem.rejectionNote && (
                    <Box sx={{ mt: selectedItem.rejectionReason ? 1.2 : 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.danger, mb: 0.3 }}>
                        {t("workflowPortal.rejectionNote", { defaultValue: "Rejection Note" })}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: NJIM.danger }}>
                        {selectedItem.rejectionNote}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            <Typography sx={{ mt: 2, mb: 1.2, fontSize: { xs: 20, md: 26 }, fontWeight: 950, color: NJIM.textPrimary }}>
              {t("workflowPortal.returnItems")} ({selectedItems.length})
            </Typography>

            <Table
              sx={{
                border: "1px solid rgba(148,163,184,0.26)",
                borderRadius: "16px",
                overflow: "hidden",
                background: "rgba(255,255,255,0.55)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid rgba(148,163,184,0.18)",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ background: "rgba(248,250,252,0.72)" }}>
                  <TableCell sx={{ fontWeight: 700 }}>{t("salesOrder.productName")}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t("returnsManagement.batchNumber")}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t("common.quantity")}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t("workflowPortal.reason")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", color: NJIM.textMuted, py: 3 }}>
                      {t("common.noData")}
                    </TableCell>
                  </TableRow>
                ) : (
                  selectedItems.map((item, idx) => (
                    <TableRow key={`${item.batchNumber}-${idx}`}>
                      <TableCell>
                        {productNameMap[String(item.itemId ?? "").trim()] || item.itemId || "-"}
                      </TableCell>
                      <TableCell>{item.batchNumber ?? "-"}</TableCell>
                      <TableCell>{item.quantity ?? 0}</TableCell>
                      <TableCell>{localizeReturnReason(item.reason, t)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {selectedItem && pendingStatuses.has(normalizeStatus(selectedItem.status)) && (
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 2 }}>
              <Button
                fullWidth
                disabled={actionLoading}
                onClick={handleApprove}
                startIcon={actionLoading ? <CircularProgress size={18} sx={{ color: NJIM.white }} /> : <CheckCircleOutlineIcon />}
                sx={{
                  ...primaryBtnSx,
                  height: 44,
                  fontWeight: 900,
                  "&.Mui-disabled": { opacity: 0.7, color: NJIM.white },
                }}
              >
                {t("workflowPortal.approveForPickup")}
              </Button>
              <Button
                fullWidth
                disabled={actionLoading}
                onClick={() => setRejectDialogOpen(true)}
                startIcon={<HighlightOffIcon />}
                sx={{
                  height: 44,
                  borderRadius: "14px",
                  background: NJIM.danger,
                  color: NJIM.white,
                  textTransform: "none",
                  fontWeight: 900,
                  boxShadow: "0 14px 34px rgba(239,68,68,0.18)",
                  "&:hover": {
                    background: NJIM.dangerHover,
                    boxShadow: "0 18px 44px rgba(239,68,68,0.22)",
                  },
                  "&.Mui-disabled": { opacity: 0.7, color: NJIM.white },
                }}
              >
                {t("workflowPortal.rejectRequest")}
              </Button>
            </Stack>
            )}
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectReason("");
          setPredefinedReason("");
        }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            boxShadow: "0 24px 60px rgba(2,6,23,0.18)",
            minWidth: { xs: 340, sm: 480 },
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: NJIM.textPrimary, pb: 0, pt: 2.5, px: 3 }}>
          {t("workflowPortal.rejectRequest")}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {/* Warning box */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 1.5,
              mb: 2.5,
              borderRadius: "12px",
              background: "rgba(234,88,12,0.07)",
              border: "1px solid rgba(234,88,12,0.25)",
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: NJIM.warning, mt: "1px", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, color: NJIM.warning, fontWeight: 600, lineHeight: 1.5 }}>
              {t("workflowPortal.rejectWarning")}
            </Typography>
          </Box>

          {/* Rejection Reason dropdown */}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary, mb: 0.75 }}>
            {t("workflowPortal.rejectTabPredefined")}
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={predefinedReason}
            onChange={(e) => setPredefinedReason(e.target.value)}
            sx={{
              borderRadius: "12px",
              background: "rgba(248,250,252,0.8)",
              mb: 2,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(148,163,184,0.35)" },
            }}
          >
            <MenuItem value="">
              <Typography sx={{ color: NJIM.textMuted, fontSize: 14 }}>
                {t("workflowPortal.selectReason")}
              </Typography>
            </MenuItem>
            {PREDEFINED_REASONS_KEYS.map((key) => (
              <MenuItem key={key} value={t(`workflowPortal.predefinedReasons.${key}`)}>
                {t(`workflowPortal.predefinedReasons.${key}`)}
              </MenuItem>
            ))}
          </Select>

          {/* Custom note text field */}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary, mb: 0.75 }}>
            {t("workflowPortal.rejectTabCustom")} <Typography component="span" sx={{ fontSize: 12, color: NJIM.textMuted, fontWeight: 500 }}>({t("common.optional", { defaultValue: "Optional" })})</Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t("workflowPortal.rejectReasonPlaceholder")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: "rgba(248,250,252,0.8)",
                "& fieldset": { borderColor: "rgba(148,163,184,0.35)" },
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectReason("");
              setPredefinedReason("");
            }}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, color: NJIM.textSecondary }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleReject}
            disabled={actionLoading || (!predefinedReason && !rejectReason.trim())}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 900,
              background: NJIM.danger,
              color: NJIM.white,
              px: 3,
              "&:hover": { background: NJIM.dangerHover },
              "&.Mui-disabled": { opacity: 0.6, color: NJIM.white },
            }}
          >
            {actionLoading
              ? <CircularProgress size={18} sx={{ color: NJIM.white }} />
              : t("workflowPortal.confirmReject")}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default WorkflowPortal;
